/*
 * ThreadLab Pack Builder
 * ----------------------
 * Custom element powering the bundle-and-save PDP experience.
 *
 * State: a selected tier (1 / 3 / 6 / 10), one {color, size} entry per slot, and an
 * active slot edited via the shared colour/size panel below the slot grid.
 * Add to cart posts all component variants in a single /cart/add.js call with
 * shared `_pack_*` line item properties; the threadlab-bundles cart transform
 * function merges them into one discounted pack line. Pricing shown here is
 * display-only — checkout maths is owned by the function reading the same
 * `threadlab.pack_config` metafield.
 *
 * Cart events mirror Impact's ProductForm flow (cart:prepare-bundled-sections,
 * variant:add, cart:change) so the existing drawer opens and refreshes.
 */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    const formatString =
      format ||
      (window.themeVariables && window.themeVariables.settings.moneyFormat) ||
      '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [integer, decimals] = value.split('.');
    const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return formatString
      .replace(/\{\{\s*amount\s*\}\}/, `${withThousands}.${decimals}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withThousands)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  class ThreadlabPackBuilder extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-pack-data]').textContent);

      this.slotsContainer = this.querySelector('[data-slots]');
      this.compareEl = this.querySelector('[data-compare]');
      this.totalEl = this.querySelector('[data-total]');
      this.saveEl = this.querySelector('[data-save]');
      this.atcButton = this.querySelector('[data-atc]');
      this.atcLabelEl = this.querySelector('[data-atc-label]');
      this.errorEl = this.querySelector('[data-error]');
      this.resetButton = this.querySelector('[data-reset]');
      this.tierHintEl = this.querySelector('[data-tier-hint]');
      this.leadPriceEl = this.querySelector('[data-lead-price]');
      this.leadAtc = this.querySelector('[data-lead-atc]');
      this.leadAtcLabel = this.querySelector('[data-lead-atc-label]');
      this.upgradeEl = this.querySelector('[data-upgrade]');
      this.shipUspEl = this.querySelector('[data-ship-usp]');
      this.sizeChartButton = this.querySelector('.tl-pack__size-chart');
      this.slotsHead = this.querySelector('[data-slots-heading]')?.closest('.tl-pack__section-head');

      // color -> {sizes: Map(size -> variant), image}
      this.byColor = new Map();
      this.data.colors.forEach((color) => {
        this.byColor.set(color, { sizes: new Map(), image: null });
      });
      this.data.variants.forEach((variant) => {
        const entry = this.byColor.get(variant.color);
        if (!entry) return;
        entry.sizes.set(variant.size, variant);
        if (!entry.image) entry.image = variant.image;
      });

      const params = new URLSearchParams(window.location.search);
      const urlTier = parseInt(params.get('pack'), 10);
      const defaultTier = parseInt(this.getAttribute('default-tier'), 10) || 1;
      const tierQty = this.data.tiers.some((t) => t.qty === urlTier) ? urlTier : defaultTier;

      // Packs start in "quick pick" mode: one size for the whole pack plus a count per colour,
      // nothing preselected. "Mix sizes" switches to the per-slot editor (mixMode).
      this.state = { tierQty: null, slots: [], activeSlot: 0, size: null, counts: {}, mixMode: false };

      this.querySelectorAll('[data-tier]').forEach((button) => {
        button.addEventListener('click', () => this._setTier(parseInt(button.dataset.tier, 10)));
      });
      this.resetButton.addEventListener('click', () => this._setTier(this.state.tierQty, true));
      // The Add button is never dead: until the pack is complete it walks the shopper to the missing step.
      this.atcButton.addEventListener('click', () => (this._complete ? this._addToCart() : this._guide()));
      if (this.leadAtc) this.leadAtc.addEventListener('click', () => this._leadAction());
      if (this.upgradeEl) this.upgradeEl.addEventListener('click', () => this._upgrade(parseInt(this.upgradeEl.dataset.qty, 10)));
      this._buildSticky();

      // The colour gallery reorders itself on variant:change; put the pack photo back after it.
      document.addEventListener('variant:change', () => setTimeout(() => this._placePackImage(false), 0));

      this._setTier(tierQty, true);
    }

    /* ------------------------------------------------------------------ state */

    get tier() {
      return this.data.tiers.find((t) => t.qty === this.state.tierQty);
    }

    _defaultSelection() {
      for (const color of this.data.colors) {
        const entry = this.byColor.get(color);
        for (const size of this.data.sizes) {
          const variant = entry.sizes.get(size);
          if (variant && variant.available) return { color, size };
        }
      }
      return { color: this.data.colors[0], size: this.data.sizes[0] };
    }

    _setTier(qty, reset) {
      const previous = this.state.slots;
      const wasSingle = this.state.tierQty === 1;
      this.state.tierQty = qty;

      if (qty > 1 && (reset || wasSingle || !this.state.mixMode)) {
        // Quick pick: keep the chosen size, trim colour counts to the new pack size.
        if (reset || wasSingle) {
          this.state.counts = {};
          this.state.mixMode = false;
        } else {
          this._trimCounts(qty);
        }
        this.state.slots = this._slotsFromCounts();
      } else if (qty === 1) {
        // Single: size is chosen by the shopper (never preselected); colour carries over or defaults.
        const color = (previous[0] && previous[0].color) || this._defaultSelection().color;
        this.state.mixMode = false;
        this.state.slots = [{ color: this._colorFor(color, this.state.size), size: this.state.size }];
      } else {
        const fill = previous.length && !reset ? previous[previous.length - 1] : this._defaultSelection();
        const slots = [];
        for (let i = 0; i < qty; i++) {
          slots.push(previous[i] && !reset ? previous[i] : { ...fill });
        }
        this.state.slots = slots;
      }
      this.state.activeSlot = qty === 1 ? 0 : this.state.activeSlot >= qty ? 0 : this.state.activeSlot;

      this.querySelectorAll('[data-tier]').forEach((button) => {
        const selected = parseInt(button.dataset.tier, 10) === qty;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      if (this.tierHintEl) {
        this.tierHintEl.textContent = this.tier.discount > 0 ? `Save ${Math.round(this.tier.discount)}%` : '';
      }

      this._renderSlots();
      this._renderSummary();
      this._placePackImage(true);
    }

    _setSlot(index, patch) {
      const slot = { ...this.state.slots[index], ...patch };
      const entry = this.byColor.get(slot.color);

      // If the size doesn't exist / is sold out in the new color, snap to the first available size.
      const variant = entry && entry.sizes.get(slot.size);
      if (!variant || !variant.available) {
        const fallback = this.data.sizes.find((size) => {
          const candidate = entry && entry.sizes.get(size);
          return candidate && candidate.available;
        });
        if (fallback) slot.size = fallback;
      }

      this.state.slots[index] = slot;
      this._renderSlots();
      this._renderSummary();
      this._placePackImage(false);
    }

    _applyToAll(index) {
      const source = this.state.slots[index];
      this.state.slots = this.state.slots.map(() => ({ ...source }));
      this._renderSlots();
      this._renderSummary();
      this._placePackImage(false);
    }

    /* ---------------------------------------------------------------- gallery */

    // Puts the product photo whose alt text is exactly "pack-<qty>" second in the gallery,
    // right after the lead model shot. "pack-<qty>-black" wins when every slot is Black.
    // Other pack photos are parked at the end; no media is ever hidden. Singles keep the
    // normal gallery order.
    _placePackImage(scrollToStart) {
      const scope = this.closest('.shopify-section') || document;
      const qty = this.state.tierQty;
      const allBlack = this.state.slots.length > 0 && this.state.slots.every((slot) => (slot.color || '').toLowerCase() === 'black');
      const wanted = qty > 1 ? [allBlack && `pack-${qty}-black`, `pack-${qty}`].filter(Boolean) : [];

      scope.querySelectorAll('.product-gallery__media-list').forEach((list) => {
        const media = Array.from(list.children).filter((el) => el.classList.contains('product-gallery__media'));
        const packMedia = media.filter((el) => /^pack-\d+(-black)?$/.test(el.dataset.mediaAlt || ''));
        if (!packMedia.length) return;

        let chosen = null;
        for (const alt of wanted) {
          chosen = packMedia.find((el) => el.dataset.mediaAlt === alt);
          if (chosen) break;
        }

        packMedia.forEach((el) => list.appendChild(el));
        if (chosen) {
          const lead = Array.from(list.children).find((el) => el.classList.contains('product-gallery__media') && el !== chosen);
          if (lead) lead.after(chosen);
          else list.prepend(chosen);
        }

        if (scrollToStart && typeof list.scrollTo === 'function') list.scrollTo({ left: 0 });
      });
    }

    _variantFor(slot) {
      const entry = this.byColor.get(slot.color);
      return entry ? entry.sizes.get(slot.size) : undefined;
    }

    /* ------------------------------------------------------------ quick pick */

    get _quickPick() {
      return this.state.tierQty > 1 && !this.state.mixMode;
    }

    _chosenCount() {
      return Object.values(this.state.counts).reduce((sum, n) => sum + n, 0);
    }

    _isAvailable(color, size) {
      const entry = this.byColor.get(color);
      const variant = entry && entry.sizes.get(size);
      return !!(variant && variant.available);
    }

    _colorAvailable(color) {
      return this.state.size
        ? this._isAvailable(color, this.state.size)
        : this.data.sizes.some((size) => this._isAvailable(color, size));
    }

    _slotsFromCounts() {
      const slots = [];
      this.data.colors.forEach((color) => {
        for (let i = 0; i < (this.state.counts[color] || 0); i++) slots.push({ color, size: this.state.size });
      });
      return slots;
    }

    _trimCounts(qty) {
      let over = this._chosenCount() - qty;
      [...this.data.colors].reverse().forEach((color) => {
        if (over <= 0 || !this.state.counts[color]) return;
        const take = Math.min(over, this.state.counts[color]);
        this.state.counts[color] -= take;
        if (!this.state.counts[color]) delete this.state.counts[color];
        over -= take;
      });
    }

    _updateQuickPick() {
      this.state.slots = this._slotsFromCounts();
      this._renderSlots();
      this._renderSummary();
      this._placePackImage(false);
    }

    _setSize(size) {
      if (this.state.tierQty === 1) {
        this.state.size = size;
        const slot = this.state.slots[0];
        this.state.slots = [{ color: this._colorFor(slot.color, size), size }];
        this._renderSlots();
        this._renderSummary();
        this._placePackImage(false);
        return;
      }

      const firstSize = !this.state.size;
      this.state.size = size;
      // Drop colours that are sold out in the new size, and say so.
      const removed = Object.keys(this.state.counts).filter((color) => !this._isAvailable(color, size));
      removed.forEach((color) => delete this.state.counts[color]);
      this.state.notice = removed.length ? `${removed.join(' and ')} ${removed.length > 1 ? 'are' : 'is'} sold out in ${size}, so we took ${removed.length > 1 ? 'them' : 'it'} out of your pack.` : '';
      this._updateQuickPick();
      this.state.notice = '';

      // First size picked and no colours yet: bring step 3 into view so the next move is obvious.
      if (firstSize && this._chosenCount() === 0) {
        const colors = this.querySelector('[data-stage="colors"]');
        if (colors && colors.getBoundingClientRect().top > window.innerHeight * 0.6) {
          window.scrollTo({ top: colors.getBoundingClientRect().top + window.scrollY - 120, behavior: 'smooth' });
        }
      }
    }

    _changeCount(color, delta) {
      if (!this.state.size) {
        this._guide();
        return;
      }
      const next = (this.state.counts[color] || 0) + delta;
      if (next < 0 || (delta > 0 && this._chosenCount() >= this.state.tierQty)) return;
      if (next === 0) delete this.state.counts[color];
      else this.state.counts[color] = next;
      this._updateQuickPick();
    }

    // Preferred colours for the "Everyday mix" preset; the first three this product
    // carries (and has in stock in the chosen size) share the pack as evenly as possible.
    _mixPreset() {
      const preference = ['Black', 'White', 'Grey', 'Natural', 'Vanilla', 'Stone Blue', 'Black Snow Wash'];
      const ordered = [
        ...preference.filter((color) => this.data.colors.includes(color)),
        ...this.data.colors.filter((color) => !preference.includes(color)),
      ].filter((color) => this._colorAvailable(color));
      const picked = ordered.slice(0, 3);
      if (picked.length < 2) return null;

      const qty = this.state.tierQty;
      const counts = {};
      picked.forEach((color, index) => {
        counts[color] = Math.floor(qty / picked.length) + (index < qty % picked.length ? 1 : 0);
      });
      return counts;
    }

    _blackPreset() {
      const black = this.data.colors.find((color) => color.toLowerCase() === 'black');
      return black && this._colorAvailable(black) ? { [black]: this.state.tierQty } : null;
    }

    _applyPreset(counts) {
      if (!this.state.size) {
        this._guide();
        return;
      }
      this.state.counts = { ...counts };
      this._updateQuickPick();
    }

    _enterMixMode() {
      const fallback = this._defaultSelection();
      const size = this.state.size || fallback.size;
      const slots = this._slotsFromCounts().map((slot) => ({ ...slot, size }));
      const fill = slots[slots.length - 1] || { color: fallback.color, size };
      while (slots.length < this.state.tierQty) slots.push({ ...fill });
      this.state.slots = slots;
      this.state.mixMode = true;
      this.state.activeSlot = 0;
      this._renderSlots();
      this._renderSummary();
      this._placePackImage(false);
    }

    // Back to quick pick, keeping the colours; the size survives only if every piece shares it.
    _exitMixMode() {
      const sizes = new Set(this.state.slots.map((slot) => slot.size));
      const counts = {};
      this.state.slots.forEach((slot) => {
        counts[slot.color] = (counts[slot.color] || 0) + 1;
      });
      this.state.size = sizes.size === 1 ? [...sizes][0] : null;
      this.state.counts = counts;
      this.state.mixMode = false;
      this._updateQuickPick();
    }

    _buildQuickPick() {
      const qty = this.state.tierQty;
      const chosen = this._chosenCount();
      const wrap = document.createElement('div');
      wrap.className = 'tl-pack__quick';

      // Step 2 (step 1 is the pack): one size for the whole pack
      const sizeStep = this._buildSizeStep();
      const pills = document.createElement('div');
      pills.className = 'tl-pack__sizes';
      this.data.sizes.forEach((size) => {
        const anyColor = this.data.colors.some((color) => this._isAvailable(color, size));
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'tl-pack__size' + (size === this.state.size ? ' is-selected' : '') + (anyColor ? '' : ' is-disabled');
        pill.disabled = !anyColor;
        pill.textContent = size;
        pill.setAttribute('aria-pressed', size === this.state.size ? 'true' : 'false');
        pill.addEventListener('click', () => this._setSize(size));
        pills.appendChild(pill);
      });
      sizeStep.appendChild(pills);
      this._appendFitNote(sizeStep);
      wrap.appendChild(sizeStep);

      // Step 3: colour counters, dimmed until a size is chosen (a tap walks the shopper back to step 2)
      const colorStep = document.createElement('div');
      colorStep.className = 'tl-pack__group tl-pack__stage' + (this.state.size ? '' : ' is-locked');
      colorStep.setAttribute('data-stage', 'colors');
      colorStep.appendChild(
        this._stepHead(3, `Pick your ${this.data.colorLabel.toLowerCase()}s`, {
          done: chosen === qty && !!this.state.size,
          aside: `<span class="tl-pack__quick-progress${chosen === qty ? ' is-complete' : ''}">${chosen} of ${qty} chosen</span>`,
        })
      );
      colorStep.appendChild(this._stepHint(this.state.size ? '' : `Choose your ${this.data.sizeLabel.toLowerCase()} first`));

      if (this.state.notice) {
        const notice = document.createElement('p');
        notice.className = 'tl-pack__notice';
        notice.setAttribute('role', 'status');
        notice.textContent = this.state.notice;
        colorStep.appendChild(notice);
      }

      const presets = [
        { label: 'Everyday mix', counts: this._mixPreset() },
        { label: 'All black', counts: this._blackPreset() },
      ].filter((preset) => preset.counts);
      if (presets.length) {
        const row = document.createElement('div');
        row.className = 'tl-pack__presets';
        presets.forEach((preset) => {
          const detail = Object.entries(preset.counts)
            .map(([color, n]) => `${n} ${color}`)
            .join(' · ');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'tl-pack__preset';
          button.innerHTML = `<strong>${escapeHtml(preset.label)}</strong><span>${escapeHtml(detail)}</span>`;
          button.addEventListener('click', () => this._applyPreset(preset.counts));
          row.appendChild(button);
        });
        colorStep.appendChild(row);
      }

      const list = document.createElement('div');
      list.className = 'tl-pack__counters';
      this.data.colors.forEach((color) => {
        const entry = this.byColor.get(color);
        const count = this.state.counts[color] || 0;
        const available = this._colorAvailable(color);
        const row = document.createElement('div');
        row.className = 'tl-pack__counter' + (count > 0 ? ' is-active' : '') + (available ? '' : ' is-disabled');
        row.innerHTML = `
          <img class="tl-pack__counter-thumb" src="${escapeHtml((entry && entry.image) || '')}" alt="" width="36" height="36" loading="lazy">
          <span class="tl-pack__counter-name">${escapeHtml(color)}${
            available ? '' : `<em>${this.state.size ? `Sold out in ${escapeHtml(this.state.size)}` : 'Sold out'}</em>`
          }</span>
          <span class="tl-pack__stepper">
            <button type="button" class="tl-pack__step" data-step="-1" aria-label="Remove one ${escapeHtml(color)}" ${count === 0 ? 'disabled' : ''}>&minus;</button>
            <span class="tl-pack__count" aria-live="polite">${count}</span>
            <button type="button" class="tl-pack__step" data-step="1" aria-label="Add one ${escapeHtml(color)}" ${
              !available || chosen >= qty ? 'disabled' : ''
            }>+</button>
          </span>
        `;
        row.querySelectorAll('[data-step]').forEach((button) => {
          button.addEventListener('click', () => this._changeCount(color, parseInt(button.dataset.step, 10)));
        });
        list.appendChild(row);
      });
      colorStep.appendChild(list);

      // Per-piece editing for packs split between people (e.g. two tees in a friend's size).
      const customise = document.createElement('div');
      customise.className = 'tl-pack__customise';
      const mix = document.createElement('button');
      mix.type = 'button';
      mix.className = 'tl-pack__mix-link';
      mix.textContent = `Customise each ${this._itemNoun(1)}`;
      mix.addEventListener('click', () => this._enterMixMode());
      const hint = document.createElement('span');
      hint.className = 'tl-pack__customise-hint';
      hint.textContent = `Set a different size or colour for any ${this._itemNoun(1)}, for example a few for a friend.`;
      customise.append(mix, hint);
      colorStep.appendChild(customise);

      wrap.appendChild(colorStep);
      return wrap;
    }

    /* ------------------------------------------------------------------ steps */

    // Numbered step heading: a circle that turns into a tick once the step is done.
    _stepHead(number, title, { value = '', aside = '', done = false } = {}) {
      const head = document.createElement('div');
      head.className = 'tl-pack__step-head' + (done ? ' is-done' : '');
      head.innerHTML = `
        <span class="tl-pack__step-num" aria-hidden="true">${number}</span>
        <span class="tl-pack__step-title">${escapeHtml(title)}${value ? ` <em>${escapeHtml(value)}</em>` : ''}</span>
        ${aside ? `<span class="tl-pack__step-aside">${aside}</span>` : ''}
      `;
      return head;
    }

    _stepHint(text) {
      const hint = document.createElement('p');
      hint.className = 'tl-pack__step-hint';
      hint.setAttribute('data-step-hint', '');
      hint.setAttribute('role', 'status');
      hint.textContent = text;
      hint.hidden = !text;
      return hint;
    }

    _buildSizeStep() {
      const step = document.createElement('div');
      step.className = 'tl-pack__group tl-pack__stage';
      step.setAttribute('data-stage', 'size');
      step.setAttribute('data-size-step', '');
      const head = this._stepHead(2, `Your ${this.data.sizeLabel.toLowerCase()}`, {
        value: this.state.size || '',
        done: !!this.state.size,
      });
      if (this.sizeChartButton) {
        const link = document.createElement('button');
        link.type = 'button';
        link.className = 'tl-pack__step-link';
        link.textContent = 'Size chart';
        link.addEventListener('click', () => this.sizeChartButton.click());
        const aside = document.createElement('span');
        aside.className = 'tl-pack__step-aside';
        aside.appendChild(link);
        head.appendChild(aside);
      }
      step.appendChild(head);
      step.appendChild(this._stepHint(''));
      return step;
    }

    _appendFitNote(step) {
      if (!this.data.fitNote) return;
      const fit = document.createElement('p');
      fit.className = 'tl-pack__fit';
      fit.textContent = this.data.fitNote;
      step.appendChild(fit);
    }

    // Single: step 2 size, step 3 colour. Nothing is preselected for size.
    _buildSingle() {
      const slot = this.state.slots[0];
      const wrap = document.createElement('div');
      wrap.className = 'tl-pack__quick';

      const sizeStep = this._buildSizeStep();
      const pills = document.createElement('div');
      pills.className = 'tl-pack__sizes';
      this.data.sizes.forEach((size) => {
        const anyColor = this.data.colors.some((color) => this._isAvailable(color, size));
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'tl-pack__size' + (size === this.state.size ? ' is-selected' : '') + (anyColor ? '' : ' is-disabled');
        pill.disabled = !anyColor;
        pill.textContent = size;
        pill.setAttribute('aria-pressed', size === this.state.size ? 'true' : 'false');
        pill.addEventListener('click', () => this._setSize(size));
        pills.appendChild(pill);
      });
      sizeStep.appendChild(pills);
      this._appendFitNote(sizeStep);
      wrap.appendChild(sizeStep);

      const colorStep = document.createElement('div');
      colorStep.className = 'tl-pack__group tl-pack__stage';
      colorStep.setAttribute('data-stage', 'colors');
      colorStep.appendChild(
        this._stepHead(3, `Your ${this.data.colorLabel.toLowerCase()}`, { value: slot.color, done: !!this.state.size })
      );
      const chips = document.createElement('div');
      chips.className = 'tl-pack__chips';
      this.data.colors.forEach((color) => {
        const entry = this.byColor.get(color);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'tl-pack__chip' + (color === slot.color ? ' is-selected' : '');
        chip.disabled = !this._colorAvailable(color);
        chip.setAttribute('aria-label', color);
        chip.setAttribute('aria-pressed', color === slot.color ? 'true' : 'false');
        chip.title = color;
        chip.innerHTML = `<img src="${escapeHtml((entry && entry.image) || '')}" alt="" width="52" height="52" loading="lazy">`;
        chip.addEventListener('click', () => {
          this.state.slots = [{ color, size: this.state.size }];
          this._renderSlots();
          this._renderSummary();
          this._placePackImage(false);
        });
        chips.appendChild(chip);
      });
      colorStep.appendChild(chips);
      wrap.appendChild(colorStep);
      return wrap;
    }

    // The colour to keep when the size changes: the same one if it is in stock in that size, else the first that is.
    _colorFor(color, size) {
      if (!size || this._isAvailable(color, size)) return color;
      return this.data.colors.find((candidate) => this._isAvailable(candidate, size)) || color;
    }

    // What is still missing, in step order, or null when the pack can go in the cart.
    _nextStep() {
      const qty = this.state.tierQty;
      if ((this._quickPick || qty === 1) && !this.state.size) {
        return { key: 'size', label: `Choose your ${this.data.sizeLabel.toLowerCase()}`, hint: `Pick your ${this.data.sizeLabel.toLowerCase()} to continue` };
      }
      if (this._quickPick) {
        const missing = qty - this._chosenCount();
        if (missing > 0) {
          const noun = this._itemNoun(missing);
          return {
            key: 'colors',
            label: `Choose ${missing} more`,
            hint: this._chosenCount() === 0 ? `Pick ${qty} ${noun} below, or tap a mix` : `Pick ${missing} more ${noun} to finish your pack`,
          };
        }
      }
      return null;
    }

    // Scroll to the missing step, flash it and say what it needs.
    _guide() {
      const next = this._nextStep();
      const target = (next && this.querySelector(`[data-stage="${next.key}"]`)) || this.slotsContainer;
      const hint = next && target.querySelector('[data-step-hint]');
      if (hint) {
        hint.textContent = next.hint;
        hint.hidden = false;
      }
      const top = target.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top, behavior: 'smooth' });
      target.classList.remove('is-flash');
      void target.offsetWidth;
      target.classList.add('is-flash');
    }

    // Pack upgrade nudge (Daniel 12 Oct 2026, 2A: every pack): "Add 4 more tees for $28.00".
    _renderUpgrade(tier) {
      if (!this.upgradeEl) return;
      const index = this.data.tiers.findIndex((t) => t.qty === tier.qty);
      const nextTier = this.data.tiers[index + 1];
      if (tier.qty === 1 || !nextTier) {
        this.upgradeEl.hidden = true;
        return;
      }
      const base = this._basePrice();
      const price = (t) => Math.round((base * t.qty * (100 - t.discount)) / 100);
      const extraQty = nextTier.qty - tier.qty;
      const extra = price(nextTier) - price(tier);
      const each = Math.round(extra / extraQty);
      this.upgradeEl.dataset.qty = nextTier.qty;
      this.upgradeEl.innerHTML = `
        <span class="tl-pack__upgrade-text">
          <strong>Add ${extraQty} more ${this._itemNoun(extraQty)} for ${formatMoney(extra)}</strong>
          <span>That's ${formatMoney(each)} a ${this._itemNoun(1)}. Upgrade to the ${nextTier.qty}-Pack.</span>
        </span>
        <span class="tl-pack__upgrade-cta">Upgrade</span>
      `;
      this.upgradeEl.hidden = false;
    }

    _upgrade(qty) {
      if (!qty) return;
      this._setTier(qty);
      if (!this._complete) this._guide();
    }

    _basePrice() {
      return (this.data.variants.find((v) => v.available) || this.data.variants[0]).price;
    }

    // Price of one piece even before its size is known (any variant of that colour), so totals never drop.
    _slotPrice(slot) {
      const exact = this._variantFor(slot);
      if (exact) return exact.price;
      const sameColor = this.data.variants.find((v) => v.color === slot.color && v.available) || this.data.variants.find((v) => v.color === slot.color);
      return sameColor ? sameColor.price : this._basePrice();
    }

    /* ------------------------------------------------------------------ render */

    _renderSlots() {
      const single = this.state.tierQty === 1;
      const stepFlow = this._quickPick || single;
      this.slotsContainer.innerHTML = '';
      this.slotsContainer.classList.toggle('tl-pack__slots--single', single);

      // The numbered steps replace "Build your pack / Reset" and the loose size chart link; the per-tee editor keeps them.
      if (this.slotsHead) this.slotsHead.hidden = stepFlow;
      this.resetButton.hidden = stepFlow;
      if (this.sizeChartButton) this.sizeChartButton.hidden = stepFlow;

      if (this._quickPick) {
        this.slotsContainer.appendChild(this._buildQuickPick());
        return;
      }

      if (single) {
        this.slotsContainer.appendChild(this._buildSingle());
        return;
      }

      if (!single && this.state.mixMode) {
        const bar = document.createElement('div');
        bar.className = 'tl-pack__mix-bar';
        bar.innerHTML = `<span>Tap a ${escapeHtml(this._itemNoun(1))} to change its ${escapeHtml(this.data.sizeLabel.toLowerCase())} or ${escapeHtml(this.data.colorLabel.toLowerCase())}</span>`;
        const back = document.createElement('button');
        back.type = 'button';
        back.textContent = 'Same size for all';
        back.addEventListener('click', () => this._exitMixMode());
        bar.appendChild(back);
        this.slotsContainer.appendChild(bar);
      }

      if (!single) {
        const grid = document.createElement('div');
        grid.className = 'tl-pack__grid';
        grid.setAttribute('role', 'radiogroup');
        this.state.slots.forEach((slot, index) => {
          grid.appendChild(this._buildTile(slot, index));
        });
        this.slotsContainer.appendChild(grid);
      }

      this.slotsContainer.appendChild(this._buildEditor(single));
    }

    _buildTile(slot, index) {
      const entry = this.byColor.get(slot.color);
      const active = this.state.activeSlot === index;

      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'tl-pack__tile' + (active ? ' is-active' : '');
      tile.style.setProperty('--slot-index', index);
      tile.setAttribute('role', 'radio');
      tile.setAttribute('aria-checked', active ? 'true' : 'false');
      tile.innerHTML = `
        <img class="tl-pack__tile-thumb" src="${escapeHtml((entry && entry.image) || '')}" alt="" width="36" height="36" loading="lazy">
        <span class="tl-pack__tile-text">
          <span class="tl-pack__tile-index">${escapeHtml(this._pieceLabel())} ${index + 1}</span>
          <span class="tl-pack__tile-desc">${escapeHtml(slot.color)} &middot; ${escapeHtml(slot.size)}</span>
        </span>
      `;
      tile.addEventListener('click', () => {
        this.state.activeSlot = index;
        this._renderSlots();
      });
      return tile;
    }

    _buildEditor(single) {
      const index = single ? 0 : Math.min(this.state.activeSlot, this.state.slots.length - 1);
      const slot = this.state.slots[index];

      const editor = document.createElement('div');
      editor.className = 'tl-pack__editor';

      if (!single) {
        const head = document.createElement('div');
        head.className = 'tl-pack__editor-head';

        const label = document.createElement('span');
        label.className = 'tl-pack__editor-label';
        label.textContent = `${this._pieceLabel()} ${index + 1}`;
        head.appendChild(label);

        const applyAll = document.createElement('button');
        applyAll.type = 'button';
        applyAll.className = 'tl-pack__apply-all';
        applyAll.textContent = `Apply to all ${this._itemNoun(2)}`;
        applyAll.addEventListener('click', () => this._applyToAll(index));
        head.appendChild(applyAll);

        editor.appendChild(head);
      }

      editor.appendChild(this._buildColorGroup(slot, index));
      editor.appendChild(this._buildSizeGroup(slot, index));

      return editor;
    }

    _buildColorGroup(slot, index) {
      const group = document.createElement('div');
      group.className = 'tl-pack__group';

      const label = document.createElement('span');
      label.className = 'tl-pack__group-label';
      label.innerHTML = `${escapeHtml(this.data.colorLabel)} <em>${escapeHtml(slot.color)}</em>`;
      group.appendChild(label);

      const chips = document.createElement('div');
      chips.className = 'tl-pack__chips';

      this.data.colors.forEach((color) => {
        const entry = this.byColor.get(color);
        const anyAvailable = this.data.sizes.some((size) => {
          const variant = entry.sizes.get(size);
          return variant && variant.available;
        });

        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'tl-pack__chip' + (color === slot.color ? ' is-selected' : '');
        chip.disabled = !anyAvailable;
        chip.setAttribute('aria-label', color);
        chip.title = color;
        chip.innerHTML = `<img src="${escapeHtml(entry.image || '')}" alt="" width="52" height="52" loading="lazy">`;
        chip.addEventListener('click', () => this._setSlot(index, { color }));
        chips.appendChild(chip);
      });

      group.appendChild(chips);
      return group;
    }

    _buildSizeGroup(slot, index) {
      const group = document.createElement('div');
      group.className = 'tl-pack__group';

      const label = document.createElement('span');
      label.className = 'tl-pack__group-label';
      label.innerHTML = `${escapeHtml(this.data.sizeLabel)} <em>${escapeHtml(slot.size)}</em>`;
      group.appendChild(label);

      const pills = document.createElement('div');
      pills.className = 'tl-pack__sizes';

      this.data.sizes.forEach((size) => {
        const entry = this.byColor.get(slot.color);
        const variant = entry && entry.sizes.get(size);
        const available = variant && variant.available;

        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'tl-pack__size' + (size === slot.size ? ' is-selected' : '') + (available ? '' : ' is-disabled');
        pill.disabled = !available;
        pill.textContent = size;
        pill.addEventListener('click', () => this._setSlot(index, { size }));
        pills.appendChild(pill);
      });

      group.appendChild(pills);
      return group;
    }

    _renderSummary() {
      const tier = this.tier;
      const next = this._nextStep();
      let compare = 0;
      let complete = !next;

      // Every piece is priced even before its size is chosen, so the total never drops while picking.
      this.state.slots.forEach((slot) => {
        compare += this._slotPrice(slot);
        const variant = this._variantFor(slot);
        if (!variant || !variant.available) complete = false;
      });
      if (this._quickPick) {
        compare += (tier.qty - this._chosenCount()) * this._basePrice();
      }

      const total = Math.round((compare * (100 - tier.discount)) / 100);
      this._complete = complete;

      this.compareEl.hidden = tier.discount === 0;
      this.saveEl.hidden = tier.discount === 0;

      if (tier.discount > 0) {
        this.compareEl.textContent = formatMoney(compare);
        this.saveEl.textContent = `You save ${formatMoney(compare - total)}`;
      }
      this.totalEl.textContent = formatMoney(total);

      const label =
        tier.qty === 1
          ? this.data.strings.addSingle
          : this.data.strings.addPack.replace('[qty]', tier.qty);
      const pending = next ? next.label : this.data.strings.unavailable;

      this.atcLabelEl.textContent = complete ? `${label} · ${formatMoney(total)}` : pending;
      this.atcButton.disabled = false;
      this.atcButton.classList.toggle('is-pending', !complete);

      this._renderUpgrade(tier);
      this._renderLead(tier, total, compare, complete, label, pending, next);
    }

    _pieceLabel() {
      const noun = this._itemNoun(1);
      return noun.charAt(0).toUpperCase() + noun.slice(1);
    }

    _itemNoun(qty) {
      const handle = this.data.handle;
      const noun = /hoodie/.test(handle) ? 'hoodie' : /crew/.test(handle) ? 'crew' : /tee/.test(handle) ? 'tee' : 'item';
      return qty === 1 ? noun : `${noun}s`;
    }

    // First-screen summary: "10 tees for $175.00 · $17.50 a tee", "Save $175.00", and a button
    // that adds the finished pack or walks the shopper to the step still missing.
    _renderLead(tier, total, compare, complete, label, pending, next) {
      if (!this.leadPriceEl) return;
      const gst = this.data.taxIncluded ? ` <span class="tl-pack__lead-tax">${this.data.taxLabel || 'incl. GST'}</span>` : '';

      if (tier.qty === 1) {
        this.leadPriceEl.innerHTML = `<strong>${formatMoney(total)}</strong>${gst}`;
      } else {
        const each = Math.round(total / tier.qty);
        this.leadPriceEl.innerHTML =
          `<strong>${tier.qty} ${this._itemNoun(tier.qty)} for ${formatMoney(total)}</strong>` +
          ` <span class="tl-pack__lead-each">· ${formatMoney(each)} a ${this._itemNoun(1)}</span>${gst}` +
          (compare > total ? ` <span class="tl-pack__lead-save">Save ${formatMoney(compare - total)}</span>` : '');
      }

      if (this.leadAtcLabel) {
        // First screen: the pack is already picked, so an unfinished pack reads as an invitation, not a chore.
        const leadPending = next && next.key === 'size' && tier.qty > 1 ? `Build your ${tier.qty}-Pack` : pending;
        this.leadAtcLabel.textContent = complete ? `${label} · ${formatMoney(total)}` : leadPending;
      }

      // "Free shipping on this pack" under the Add buttons once the pack's list price reaches the
      // retail threshold (0 for trade and outside Australia, so it never shows there).
      const threshold = this.data.freeShipThreshold || 0;
      const shipsFree = threshold > 0 && tier.qty > 1 && total >= threshold;
      this.querySelectorAll('[data-free-ship-line]').forEach((line) => {
        line.hidden = !shipsFree;
      });
      if (this.shipUspEl) {
        this.shipUspEl.textContent = shipsFree ? 'Free shipping on this pack' : this.shipUspEl.dataset.default;
        this.shipUspEl.classList.toggle('is-met', shipsFree);
      }

      if (this.sticky) {
        this.sticky.title.textContent =
          tier.qty === 1 ? `${this._pieceLabel()} · ${formatMoney(total)}` : `${tier.qty}-Pack · ${formatMoney(total)}`;
        this.sticky.sub.textContent = tier.qty === 1 ? '' : `${formatMoney(Math.round(total / tier.qty))} a ${this._itemNoun(1)}`;
        this.sticky.button.textContent = complete ? 'Add to cart' : next && next.key === 'size' ? 'Choose size' : pending;
        this.sticky.thumb.src = this._stickyThumb();
      }
    }

    /* ------------------------------------------------------------ sticky bar */

    // Phone-only bar pinned to the bottom once both Add buttons (first screen and builder)
    // have scrolled out of view. Its button runs the same action as the first-screen one.
    _buildSticky() {
      if (!this.leadAtc) return;
      const bar = document.createElement('div');
      bar.className = 'tl-sticky';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = `
        <img class="tl-sticky__thumb" alt="" width="44" height="44">
        <span class="tl-sticky__text"><strong class="tl-sticky__title"></strong><span class="tl-sticky__sub"></span></span>
        <button type="button" class="tl-sticky__button button" tabindex="-1"></button>
      `;
      document.body.appendChild(bar);
      this.sticky = {
        bar,
        thumb: bar.querySelector('.tl-sticky__thumb'),
        title: bar.querySelector('.tl-sticky__title'),
        sub: bar.querySelector('.tl-sticky__sub'),
        button: bar.querySelector('.tl-sticky__button'),
      };
      this.sticky.button.addEventListener('click', () => this._leadAction());

      const inView = new Map();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => inView.set(entry.target, entry.isIntersecting));
        const anyVisible = [...inView.values()].some(Boolean);
        const pastLead = this.leadAtc.getBoundingClientRect().bottom < 0;
        const show = !anyVisible && pastLead;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        this.sticky.button.tabIndex = show ? 0 : -1;
        document.documentElement.classList.toggle('tl-sticky-on', show);
      });
      observer.observe(this.leadAtc);
      observer.observe(this.atcButton);
    }

    _stickyThumb() {
      const scope = this.closest('.shopify-section') || document;
      const packImg = scope.querySelector('.product-gallery__media-list > .product-gallery__media[data-media-alt^="pack-"] img');
      const first = this.state.slots[0] && this._variantFor(this.state.slots[0]);
      const src = (this.state.tierQty > 1 && packImg && (packImg.currentSrc || packImg.src)) || (first && first.image) || this.data.variants[0].image;
      return src || '';
    }

    _leadAction() {
      if (this._complete) {
        this._addToCart();
        return;
      }
      this._guide();
    }

    /* ------------------------------------------------------------------ cart */

    async _addToCart() {
      this.errorEl.hidden = true;

      const tier = this.tier;
      if (!this._complete || this.state.slots.length !== tier.qty) return;
      const packId =`${this.data.handle}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      // Aggregate duplicate variants so Shopify's line merging stays predictable.
      const quantities = new Map();
      for (const slot of this.state.slots) {
        const variant = this._variantFor(slot);
        if (!variant) return;
        quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
      }

      const items = Array.from(quantities, ([id, quantity]) => ({
        id,
        quantity,
        ...(tier.qty > 1 && {
          properties: {
            _pack_id: packId,
            _pack_handle: this.data.handle,
            _pack_qty: String(tier.qty),
          },
        }),
      }));

      this.atcButton.setAttribute('disabled', 'disabled');
      this.atcButton.setAttribute('aria-busy', 'true');

      try {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: sectionsToBundle.join(',') }),
        });

        const responseJson = await response.json();

        if (!response.ok) {
          this.errorEl.textContent = responseJson.description || responseJson.message || 'Something went wrong. Please try again.';
          this.errorEl.hidden = false;
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        if (tier.qty > 1) this._trackPackAdd(tier, items, responseJson);

        if (window.themeVariables && window.themeVariables.settings.cartType === 'page') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent['sections'] = responseJson['sections'];

        this.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: responseJson.items || [responseJson], cart: cartContent },
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent },
          })
        );
      } catch (error) {
        this.errorEl.textContent = 'Could not add to cart. Please try again.';
        this.errorEl.hidden = false;
      } finally {
        this.atcButton.removeAttribute('aria-busy');
        this.atcButton.removeAttribute('disabled');
        this._renderSummary();
      }
    }

    // Every pack add (3/6/10) is reported to Meta once, as the custom event threadlab:pack_add, which the
    // "ThreadLab Meta (retail only)" custom pixel sends as AddToCart. Shopify's own product_added_to_cart is no
    // good for packs: a mixed colour/size pack sends several variants and emits nothing, and a one-variant pack
    // emits the merged pack line with value = pack price x pack size (a $178 3-pack reported as $535). The pixel
    // drops those pack lines, so nothing is counted twice. Singles are left to Shopify.
    // value = the merged pack line as the cart shows it (retail, GST incl.); content_ids = the tee/hoodie variants
    // in the pack, the same ids ViewContent and Purchase use.
    _trackPackAdd(tier, items, responseJson) {
      try {
        const lines = responseJson.items || [];
        if (!lines.length) return;

        const cents = lines.reduce((sum, line) => sum + (line.final_line_price ?? line.line_price ?? 0), 0);
        const payload = {
          content_type: 'product',
          content_ids: items.map((item) => String(item.id)),
          contents: items.map((item) => ({ id: String(item.id), quantity: item.quantity })),
          content_name: `${this.data.title} ${tier.qty}-Pack`,
          content_category: 'Pack',
          currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD',
          value: cents / 100,
          num_items: tier.qty,
        };

        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish('threadlab:pack_add', payload);
        }
      } catch (error) {
        // Tracking must never break add to cart.
      }
    }
  }

  if (!window.customElements.get('threadlab-pack-builder')) {
    window.customElements.define('threadlab-pack-builder', ThreadlabPackBuilder);
  }
})();
